/**
 * platform-module-contract.ts
 * Zod contracts for the commercial record of each (company, module) deal —
 * platform-company-hub T1.
 *
 * RigX only RECORDS what a client pays (no invoicing): a fixed monthly USD
 * amount per module per company, plus an append-only change history. The
 * `module.<key>.enabled` feature-flag override remains the runtime source for
 * entitlement; platform writes keep both in sync in one transaction.
 *
 * Module keys derive from PRODUCT_MODULE_KEYS — never a second hand-copied list.
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 */
import { z } from 'zod';
import { PRODUCT_MODULE_KEYS } from './platform-company.js';

export const ModuleKeySchema = z.enum(PRODUCT_MODULE_KEYS);

/** numeric(12,2) ceiling. */
const MAX_MONTHLY_PRICE_USD = 9_999_999_999.99;

/** Non-negative USD amount with at most 2 decimals, fitting numeric(12,2). */
export const MonthlyPriceUsdSchema = z
  .number({ invalid_type_error: 'monthlyPriceUsd must be a number' })
  .min(0, 'monthlyPriceUsd must be >= 0')
  .max(MAX_MONTHLY_PRICE_USD, 'monthlyPriceUsd is too large')
  .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6, {
    message: 'monthlyPriceUsd must have at most 2 decimals',
  });

/** Calendar date `YYYY-MM-DD`. */
export const ContractDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'since must be a date in YYYY-MM-DD format')
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), { message: 'since must be a valid date' });

/** One module's current commercial state for a company. */
export const ModuleContractItemSchema = z.object({
  moduleKey: ModuleKeySchema,
  enabled: z.boolean(),
  /** Fixed monthly USD price; null = not priced (counted as 0 in totals). */
  monthlyPriceUsd: z.number().nullable(),
  /** Date the module was contracted/enabled (YYYY-MM-DD); null when never contracted. */
  since: z.string().nullable(),
});

export const ModuleContractsResponseSchema = z.object({
  companyId: z.string().uuid(),
  contracts: z.array(ModuleContractItemSchema),
});

/** One module change in a PUT body. Omitted price/since leave the stored values untouched. */
export const SetModuleContractEntrySchema = z.object({
  moduleKey: ModuleKeySchema,
  enabled: z.boolean({ required_error: 'enabled is required' }),
  monthlyPriceUsd: MonthlyPriceUsdSchema.nullable().optional(),
  since: ContractDateSchema.optional(),
});

/** PUT /platform/companies/:id/module-contracts — only listed modules change. */
export const SetModuleContractsBodySchema = z.object({
  contracts: z
    .array(SetModuleContractEntrySchema, { required_error: 'contracts is required' })
    .refine((entries) => new Set(entries.map((e) => e.moduleKey)).size === entries.length, {
      message: 'contracts must not repeat a moduleKey',
    }),
});

export const ModuleContractHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  moduleKey: ModuleKeySchema,
  /** ISO 8601 timestamp. */
  changedAt: z.string(),
  /** Platform user id, when known. */
  changedBy: z.string().uuid().nullable(),
  oldEnabled: z.boolean(),
  newEnabled: z.boolean(),
  oldMonthlyPriceUsd: z.number().nullable(),
  newMonthlyPriceUsd: z.number().nullable(),
  /** Contract start dates (YYYY-MM-DD); old is null when the module had no contract row. */
  oldSince: z.string().nullable(),
  newSince: z.string().nullable(),
});

/** Newest first. */
export const ModuleContractHistoryResponseSchema = z.object({
  companyId: z.string().uuid(),
  history: z.array(ModuleContractHistoryEntrySchema),
});

export type ModuleContractItem = z.infer<typeof ModuleContractItemSchema>;
export type ModuleContractsResponse = z.infer<typeof ModuleContractsResponseSchema>;
export type SetModuleContractEntry = z.infer<typeof SetModuleContractEntrySchema>;
export type SetModuleContractsBody = z.infer<typeof SetModuleContractsBodySchema>;
export type ModuleContractHistoryEntry = z.infer<typeof ModuleContractHistoryEntrySchema>;
export type ModuleContractHistoryResponse = z.infer<typeof ModuleContractHistoryResponseSchema>;
